import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Save, X, Check, Image as ImageIcon, RefreshCw, Search, Trash2, Link as LinkIcon, AlertTriangle, Send } from "lucide-react";

const CAT_TO_CLASS = {
  connectors: "risk-hl-connectors",
  vocabulary: "risk-hl-vocabulary",
  typography: "risk-hl-typography",
};

export default function DraftDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState(null);
  const [title, setTitle] = useState("");
  const [contentText, setContentText] = useState(""); // plain-text-ish paragraphs (editable)
  const [chapo, setChapo] = useState("");
  const [showHl, setShowHl] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [sendingToWp, setSendingToWp] = useState(false);

  useEffect(() => {
    api.get(`/drafts/${id}`).then((r) => {
      setDraft(r.data);
      setTitle(r.data.title || "");
      setChapo(r.data.chapo || "");
      // Convert content_html to editable text: strip <p> tags and use \n\n as separator
      const html = r.data.content_html || "";
      const paragraphs = html
        .replace(/<\/p>/gi, "\n\n")
        .replace(/<p[^>]*>/gi, "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .trim();
      setContentText(paragraphs);
    });
  }, [id]);

  const contentAsHtml = useMemo(() => {
    return contentText
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
      .join("\n");
  }, [contentText]);

  const highlightedBody = useMemo(() => {
    if (!draft || !showHl) return renderPlainBody(contentText);
    return renderHighlightedBody(contentText, draft.risk_matches || []);
  }, [contentText, draft, showHl]);

  if (!draft) {
    return (
      <div className="p-8 text-sm text-[var(--text-muted)] font-mono">Chargement…</div>
    );
  }

  const saveEdits = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/drafts/${id}`, {
        title, chapo, content_html: contentAsHtml,
      });
      setDraft(r.data);
      toast.success("Modifications enregistrées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  const publish = async () => {
    if (draft.status === "published") return;
    if (!draft.featured_media_id) {
      toast.error("Image de couverture manquante — ajoute une image (banque ou URL) avant de publier.");
      return;
    }
    // Save first if user edited
    setPublishing(true);
    try {
      await api.patch(`/drafts/${id}`, {
        title, chapo, content_html: contentAsHtml,
      });
      await api.post(`/drafts/${id}/publish`);
      toast.success("Publié sur WordPress");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication échouée");
    } finally { setPublishing(false); }
  };

  const sendToWpDraft = async () => {
    if (draft.status !== "pending") return;
    setSendingToWp(true);
    try {
      const r = await api.post(`/drafts/${id}/save-to-wp`, {
        title, chapo, content_html: contentAsHtml,
      });
      setDraft(r.data);
      toast.success("Envoyé en brouillon WordPress — tu peux le relire dans WP.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi vers WordPress échoué");
    } finally { setSendingToWp(false); }
  };

  const reject = async () => {
    if (!window.confirm("Rejeter ce brouillon ?")) return;
    try {
      await api.post(`/drafts/${id}/reject`);
      toast.success("Brouillon rejeté");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejet impossible");
    }
  };

  const readOnly = draft.status !== "pending";

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="draft-detail">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-[var(--border-default)]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            data-testid="detail-back"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-[var(--bg-sidebar)]"
            aria-label="Retour"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <div className="flex-1 text-center text-[11px] font-mono uppercase tracking-wider text-[var(--text-muted)] truncate">
            {draft.feed_name}
          </div>
          {draft.wp_edit_url && (
            <a
              href={draft.wp_edit_url}
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-[var(--bg-sidebar)]"
              aria-label="Ouvrir dans WordPress"
              data-testid="detail-open-wp"
            >
              <ExternalLink className="w-5 h-5" strokeWidth={1.75} />
            </a>
          )}
        </div>
      </div>

      <div className="flex-1 max-w-2xl w-full mx-auto pb-[140px]">
        {/* Meta */}
        <div className="px-5 pt-5 pb-3 text-[11px] font-mono text-[var(--text-muted)] flex items-center gap-2 flex-wrap">
          <RiskChip level={draft.risk_level} count={draft.risk_score} />
          {draft.needs_verification && (
            <span className="text-[var(--accent)] italic" data-testid="detail-needs-verif">
              · à vérifier
            </span>
          )}
          <span>· {new Date(draft.created_at).toLocaleString("fr-FR")}</span>
          {draft.status !== "pending" && (
            <span className="ml-auto uppercase tracking-wider">
              {draft.status === "published" ? "publié" : "rejeté"}
            </span>
          )}
        </div>

        {/* Featured image block */}
        <FeaturedImageBlock
          draft={draft}
          readOnly={readOnly}
          onUpdate={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        />

        {/* Title (editable) */}
        <div className="px-5">
          <label className="caption block mb-1">Titre</label>
          <textarea
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={readOnly}
            rows={2}
            data-testid="detail-title-input"
            className="w-full font-headings text-3xl leading-[1.1] tracking-tight bg-transparent border-none focus:outline-none resize-none disabled:text-[var(--text-secondary)]"
          />
        </div>

        {/* Chapô */}
        <div className="px-5 mt-4">
          <label className="caption block mb-1">Chapô</label>
          <textarea
            value={chapo}
            onChange={(e) => setChapo(e.target.value)}
            disabled={readOnly}
            rows={3}
            data-testid="detail-chapo-input"
            className="w-full text-[15px] leading-relaxed font-semibold bg-transparent border-none focus:outline-none resize-none disabled:text-[var(--text-secondary)]"
          />
        </div>

        {/* Highlight toggle */}
        <div className="px-5 mt-6 flex items-center justify-between border-y border-[var(--border-default)] py-2.5">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-muted)]">
            Corps de l&apos;article
          </div>
          <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider">
            <input
              type="checkbox"
              checked={showHl}
              onChange={(e) => setShowHl(e.target.checked)}
              data-testid="detail-toggle-hl"
              className="accent-black"
            />
            surligner
          </label>
        </div>

        {/* Body — editable textarea + highlighted preview stacked */}
        {readOnly ? (
          <div
            className="article-body px-5 py-5"
            dangerouslySetInnerHTML={{ __html: highlightedBody }}
          />
        ) : (
          <>
            <div
              className="article-body px-5 py-5 border-b border-[var(--border-default)]"
              dangerouslySetInnerHTML={{ __html: highlightedBody }}
              data-testid="detail-preview"
            />
            <div className="px-5 py-4">
              <label className="caption block mb-1">Édition</label>
              <textarea
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                data-testid="detail-body-textarea"
                rows={14}
                className="w-full font-mono text-[13px] leading-relaxed bg-[var(--bg-sidebar)] border border-[var(--border-default)] rounded-none p-3 focus:outline-none focus:border-black"
                placeholder="Un paragraphe par bloc, séparés par une ligne vide."
              />
              <p className="mt-1 text-[11px] text-[var(--text-muted)] font-mono">
                Séparer les paragraphes par une ligne vide.
              </p>
            </div>
          </>
        )}

        {/* Risk breakdown */}
        {(draft.risk_matches || []).length > 0 && (
          <div className="px-5 py-4 border-t border-[var(--border-default)]" data-testid="detail-risk-breakdown">
            <div className="caption mb-2">Marqueurs détectés</div>
            <div className="flex flex-wrap gap-1.5">
              {draft.risk_matches.map((m, i) => (
                <span
                  key={i}
                  className={`px-2 py-1 text-[11px] font-mono ${
                    m.category === "typography"
                      ? "bg-[rgba(139,47,47,0.1)] text-[var(--accent-strong)]"
                      : "bg-[var(--accent-soft)] text-[var(--accent)]"
                  }`}
                >
                  {m.term === "—" ? "tiret cadratin" : m.term === "–" ? "tiret demi-cadratin" : m.term}
                  <span className="opacity-60"> ×{m.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom actions */}
      {!readOnly && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[var(--border-default)]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
            <button
              onClick={reject}
              data-testid="detail-reject"
              className="min-h-[48px] min-w-[48px] flex items-center justify-center border border-[var(--border-default)] hover:border-[var(--accent-strong)] hover:text-[var(--accent-strong)] transition-colors"
              aria-label="Rejeter"
            >
              <X className="w-5 h-5" strokeWidth={1.75} />
            </button>
            <button
              onClick={saveEdits}
              disabled={saving}
              data-testid="detail-save"
              title="Enregistrer les édits (local uniquement)"
              className="min-h-[48px] px-3 border border-[var(--border-default)] hover:border-black text-sm uppercase tracking-widest font-mono transition-colors disabled:opacity-60 flex items-center gap-2"
            >
              <Save className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">{saving ? "…" : "Enregistrer"}</span>
            </button>
            <button
              onClick={sendToWpDraft}
              disabled={sendingToWp}
              data-testid="detail-send-to-wp"
              title="Envoyer les édits vers WordPress en gardant le statut brouillon"
              className="min-h-[48px] px-3 border border-black text-sm uppercase tracking-widest font-mono hover:bg-black hover:text-white transition-colors disabled:opacity-60 flex items-center gap-2"
            >
              <Send className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">{sendingToWp ? "Envoi…" : "Brouillon WP"}</span>
            </button>
            <button
              onClick={publish}
              disabled={publishing || !draft.featured_media_id}
              title={!draft.featured_media_id ? "Image de couverture requise" : undefined}
              data-testid="detail-publish"
              className="flex-1 min-h-[48px] bg-black text-white text-sm uppercase tracking-widest font-medium hover:bg-[#222] transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" strokeWidth={1.75} />
              {publishing ? "Publication…" : draft.featured_media_id ? "Publier" : "Image requise"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------- helpers -------------------------

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderPlainBody(text) {
  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

/**
 * Rebuild the paragraph HTML with <mark> spans around detected terms.
 * We compute positions client-side on the rendered plain text to ensure the
 * highlights stay aligned even when the user edits the body.
 */
function renderHighlightedBody(text, matches) {
  const ranges = [];
  for (const m of matches || []) {
    const term = m.term || "";
    if (!term) continue;
    // Word-boundary for connectors/vocabulary, literal for typography chars.
    const isTypo = m.category === "typography";
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = isTypo
      ? new RegExp(escaped, "g")
      : new RegExp(`(?<![A-Za-zÀ-ÿ])${escaped}(?![A-Za-zÀ-ÿ])`, "gi");
    let match;
    while ((match = pattern.exec(text)) !== null) {
      ranges.push({ start: match.index, end: match.index + match[0].length, category: m.category });
      if (match.index === pattern.lastIndex) pattern.lastIndex++;
    }
  }
  ranges.sort((a, b) => a.start - b.start);

  // Merge overlaps (first wins)
  const clean = [];
  let lastEnd = -1;
  for (const r of ranges) {
    if (r.start < lastEnd) continue;
    clean.push(r);
    lastEnd = r.end;
  }

  let out = "";
  let cursor = 0;
  for (const r of clean) {
    if (r.start > cursor) out += escapeHtml(text.slice(cursor, r.start));
    const cls = CAT_TO_CLASS[r.category] || "risk-hl-connectors";
    out += `<mark class="${cls}">${escapeHtml(text.slice(r.start, r.end))}</mark>`;
    cursor = r.end;
  }
  if (cursor < text.length) out += escapeHtml(text.slice(cursor));

  return out
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

function RiskChip({ level, count }) {
  const label = { green: "vert", orange: "orange", red: "rouge" }[level] || "—";
  const cls =
    level === "red"
      ? "text-[var(--accent-strong)]"
      : level === "orange"
        ? "text-[var(--accent)]"
        : "text-[var(--status-success)]";
  return (
    <span className={`${cls}`} data-testid="detail-risk-chip">
      risque {label} · {count} marqueur{count > 1 ? "s" : ""}
    </span>
  );
}

// ------------------------- Featured image block -------------------------

function FeaturedImageBlock({ draft, readOnly, onUpdate }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyingUrl, setApplyingUrl] = useState(null);
  const [providerNotSet, setProviderNotSet] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const openDrawer = async () => {
    setDrawerOpen(true);
    if (candidates.length === 0) await runSearch();
  };

  const runSearch = async (q) => {
    setLoading(true);
    setProviderNotSet(false);
    try {
      const params = q ? { q } : {};
      const r = await api.get(`/drafts/${draft.id}/image-candidates`, { params });
      setCandidates(r.data.candidates || []);
      if (!q) setQuery(r.data.query || "");
      if ((r.data.candidates || []).length === 0) {
        toast.message("Aucune image trouvée pour cette requête.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Recherche impossible";
      if (detail.includes("fournisseur")) setProviderNotSet(true);
      else toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const applyCandidate = async (c) => {
    setApplyingUrl(c.url);
    try {
      const r = await api.post(`/drafts/${draft.id}/replace-image`, {
        url: c.url,
        attribution: c.attribution || "",
        attribution_url: c.attribution_url || "",
      });
      onUpdate({
        featured_media_id: r.data.media_id,
        featured_media_url: r.data.url,
        featured_media_attribution: r.data.attribution,
        featured_media_source: r.data.attribution_url,
      });
      toast.success("Image mise à jour sur WordPress");
      setDrawerOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'appliquer cette image");
    } finally {
      setApplyingUrl(null);
    }
  };

  const applyFromUrl = async () => {
    const u = urlValue.trim();
    if (!u) {
      toast.error("Colle une URL d'image");
      return;
    }
    setUrlLoading(true);
    try {
      const r = await api.post(`/drafts/${draft.id}/image-from-url`, { url: u });
      onUpdate({
        featured_media_id: r.data.media_id,
        featured_media_url: r.data.url,
        featured_media_attribution: "",
        featured_media_source: "",
      });
      toast.success("Image téléversée (converti en JPG)");
      setUrlValue("");
      setDrawerOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "URL non valide ou image injoignable");
    } finally {
      setUrlLoading(false);
    }
  };

  const removeImage = async () => {
    if (!window.confirm("Retirer l'image de couverture ? Tu devras en ajouter une autre avant de publier.")) return;
    setRemoving(true);
    try {
      await api.delete(`/drafts/${draft.id}/image`);
      onUpdate({
        featured_media_id: null,
        featured_media_url: null,
        featured_media_attribution: null,
        featured_media_source: null,
      });
      toast.success("Image retirée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de retirer l'image");
    } finally {
      setRemoving(false);
    }
  };

  const hasImage = !!draft.featured_media_url;
  const canEdit = !readOnly;

  return (
    <div className="px-5 pt-2 pb-4" data-testid="featured-image-block">
      {/* Warning banner when no image */}
      {!hasImage && draft.status === "pending" && (
        <div
          className="mb-3 flex items-start gap-2 border border-[var(--accent-strong)] bg-[rgba(139,47,47,0.06)] px-3 py-2.5 text-[12px] leading-relaxed"
          data-testid="no-image-banner"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[var(--accent-strong)]" strokeWidth={1.75} />
          <div className="text-[var(--accent-strong)]">
            <strong>Image de couverture requise.</strong> Aucun article ne peut être publié sans image.
            Ajoute-en une via la banque d&apos;images ou en collant une URL.
          </div>
        </div>
      )}

      {hasImage ? (
        <figure className="border border-[var(--border-default)] bg-[var(--bg-sidebar)]">
          <img
            src={draft.featured_media_url}
            alt={draft.title}
            data-testid="featured-image-current"
            className="w-full h-auto max-h-[280px] object-cover block"
          />
          <figcaption className="flex items-center justify-between gap-2 px-3 py-2 text-[11px] font-mono text-[var(--text-muted)]">
            <span className="truncate">
              {draft.featured_media_attribution || "Image mise en avant"}
              {draft.featured_media_source ? (
                <>
                  {" · "}
                  <a
                    href={draft.featured_media_source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[var(--text-primary)]"
                  >
                    source
                  </a>
                </>
              ) : null}
            </span>
            {canEdit && (
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={openDrawer}
                  data-testid="featured-image-change"
                  className="min-h-[36px] px-3 border border-[var(--border-default)] hover:border-black uppercase tracking-widest text-[10px] flex items-center gap-1.5"
                >
                  <RefreshCw className="w-3 h-3" strokeWidth={1.75} /> Changer
                </button>
                <button
                  onClick={removeImage}
                  disabled={removing}
                  data-testid="featured-image-remove"
                  className="min-h-[36px] min-w-[36px] flex items-center justify-center border border-[var(--border-default)] hover:border-[var(--accent-strong)] hover:text-[var(--accent-strong)] transition-colors disabled:opacity-50"
                  aria-label="Retirer l'image"
                  title="Retirer l'image"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              </div>
            )}
          </figcaption>
        </figure>
      ) : (
        canEdit && (
          <button
            onClick={openDrawer}
            data-testid="featured-image-add"
            className="w-full min-h-[80px] border border-dashed border-[var(--border-default)] bg-[var(--bg-sidebar)] hover:border-black flex items-center justify-center gap-2 text-[12px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <ImageIcon className="w-4 h-4" strokeWidth={1.75} />
            Ajouter une image de couverture
          </button>
        )
      )}

      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
          onClick={() => setDrawerOpen(false)}
          data-testid="image-drawer"
        >
          <div
            className="w-full sm:max-w-2xl bg-white border-t sm:border border-[var(--border-default)] max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer header */}
            <div className="flex items-center gap-2 border-b border-[var(--border-default)] px-4 py-3">
              <ImageIcon className="w-4 h-4" strokeWidth={1.75} />
              <div className="text-[11px] font-mono uppercase tracking-wider flex-1">
                Choisir une image
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="image-drawer-close"
                className="min-h-[36px] min-w-[36px] flex items-center justify-center hover:bg-[var(--bg-sidebar)]"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* URL paste bar */}
            <div className="border-b border-[var(--border-default)] px-4 py-3">
              <label className="caption block mb-1">Coller une URL d&apos;image</label>
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-[var(--text-muted)] shrink-0" strokeWidth={1.75} />
                <input
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyFromUrl();
                    }
                  }}
                  onPaste={(e) => {
                    const p = e.clipboardData.getData("text");
                    if (/^https?:\/\/\S+/i.test(p.trim())) {
                      setUrlValue(p.trim());
                      // Auto-submit on paste, small delay so state settles
                      setTimeout(() => applyFromUrl(), 50);
                      e.preventDefault();
                    }
                  }}
                  placeholder="https://exemple.com/photo.png"
                  data-testid="image-url-input"
                  className="flex-1 min-h-[40px] border-none focus:outline-none font-mono text-sm bg-transparent"
                />
                <button
                  onClick={applyFromUrl}
                  disabled={urlLoading}
                  data-testid="image-url-submit"
                  className="min-h-[40px] px-3 bg-[var(--accent)] text-white text-[11px] uppercase tracking-widest font-mono hover:opacity-90 disabled:opacity-60"
                >
                  {urlLoading ? "…" : "Charger"}
                </button>
              </div>
              <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">
                L&apos;image est téléchargée, convertie en JPG puis téléversée dans WordPress. Titre, ALT, légende et description sont renseignés avec le titre de l&apos;article.
              </p>
            </div>

            {/* Search bar */}
            <div className="border-b border-[var(--border-default)] px-4 py-3">
              <label className="caption block mb-1">Ou chercher dans la banque</label>
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-[var(--text-muted)] shrink-0" strokeWidth={1.75} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      runSearch(query);
                    }
                  }}
                  placeholder="Mots-clés (ex : Kiev drone frappe)"
                  data-testid="image-search-input"
                  className="flex-1 min-h-[40px] border-none focus:outline-none font-mono text-sm bg-transparent"
                />
                <button
                  onClick={() => runSearch(query)}
                  disabled={loading}
                  data-testid="image-search-btn"
                  className="min-h-[40px] px-3 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60"
                >
                  {loading ? "…" : "Chercher"}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-3">
              {providerNotSet ? (
                <div className="p-6 text-center text-[13px] font-mono text-[var(--text-muted)]">
                  Aucun fournisseur d&apos;image configuré.
                  <br />
                  Va dans <span className="text-[var(--text-primary)]">Réglages → Image de couverture</span>
                  {" "}pour choisir Pexels ou Unsplash — ou utilise le champ URL ci-dessus.
                </div>
              ) : loading ? (
                <div className="p-6 text-center text-[13px] font-mono text-[var(--text-muted)]">Recherche…</div>
              ) : candidates.length === 0 ? (
                <div className="p-6 text-center text-[13px] font-mono text-[var(--text-muted)]">
                  Aucun résultat. Essaie d&apos;autres mots-clés ou colle une URL.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2" data-testid="image-candidates-grid">
                  {candidates.map((c, i) => {
                    const applying = applyingUrl === c.url;
                    return (
                      <button
                        key={`${c.provider_id}-${i}`}
                        onClick={() => applyCandidate(c)}
                        disabled={!!applyingUrl}
                        data-testid={`image-candidate-${i}`}
                        className="group relative border border-[var(--border-default)] hover:border-black overflow-hidden text-left disabled:opacity-60"
                      >
                        <img
                          src={c.url}
                          alt={c.attribution || "candidat"}
                          className="w-full h-32 object-cover block"
                          loading="lazy"
                        />
                        <div className="px-2 py-1.5 text-[10px] font-mono text-[var(--text-muted)] truncate">
                          {c.attribution}
                        </div>
                        {applying && (
                          <div className="absolute inset-0 bg-white/80 flex items-center justify-center text-[11px] font-mono uppercase tracking-widest">
                            Téléversement…
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-[var(--border-default)] px-4 py-2 text-[10px] font-mono text-[var(--text-muted)]">
              Cliquer sur une vignette téléverse l&apos;image dans WordPress et l&apos;attache
              à ce brouillon.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
